/**
 * Utility functions to compare prior and new aggregates and identify differences
 */

/**
 * Helper to remove extension fields from objects/arrays for comparison
 */
const removeExtensions = (obj) => {
  if (obj == null) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => removeExtensions(item));
  }

  if (typeof obj === "object") {
    const { extension, ...rest } = obj;
    const cleaned = {};
    for (const key in rest) {
      cleaned[key] = removeExtensions(rest[key]);
    }
    return cleaned;
  }

  return obj;
};

/**
 * Filter out addresses/telecoms that are additionalDataFromRecords
 * These are supplementary data brought in from member records and should
 * not be considered as demographic changes for diff purposes.
 */
const filterAdditionalDataFromRecords = (items) => {
  if (!Array.isArray(items)) return items;
  return items.filter((item) => {
    if (!item || !item.extension) return true;
    return !item.extension.some(
      (ext) =>
        ext.url === "additionalDataFromRecords" && ext.valueBoolean === true,
    );
  });
};

/**
 * Deep compare two values and return if they are different
 * Extensions are ignored in the comparison
 */
const isDifferent = (val1, val2) => {
  if (val1 === val2) return false;
  if (val1 == null || val2 == null) return val1 !== val2;

  // Remove extensions before comparison
  const cleaned1 = removeExtensions(val1);
  const cleaned2 = removeExtensions(val2);

  // For arrays, compare as JSON strings (simple but effective for FHIR data)
  if (Array.isArray(cleaned1) && Array.isArray(cleaned2)) {
    return JSON.stringify(cleaned1) !== JSON.stringify(cleaned2);
  }

  // For objects, compare as JSON strings
  if (typeof cleaned1 === "object" && typeof cleaned2 === "object") {
    return JSON.stringify(cleaned1) !== JSON.stringify(cleaned2);
  }

  return cleaned1 !== cleaned2;
};

/**
 * Compare two FHIR patients and return a diff map
 * @param {Object} priorPatient - The prior aggregate patient data
 * @param {Object} newPatient - The new/override patient data
 * @returns {Object} Diff map with paths as keys and {prior, new, changeType} as values
 */
export const comparePatients = (priorPatient, newPatient) => {
  if (!priorPatient || !newPatient) return {};

  const diffMap = {};

  // Helper to determine change type
  const getChangeType = (prior, current) => {
    if (!prior && current) return "added";
    if (prior && !current) return "removed";
    if (prior && current && isDifferent(prior, current)) return "changed";
    return null;
  };

  // Compare names
  const nameChangeType = getChangeType(priorPatient.name, newPatient.name);
  if (nameChangeType) {
    diffMap.name = {
      prior: priorPatient.name,
      new: newPatient.name,
      isDifferent: true,
      changeType: nameChangeType,
    };
  }

  // Compare gender
  const genderChangeType = getChangeType(
    priorPatient.gender,
    newPatient.gender,
  );
  if (genderChangeType) {
    diffMap.gender = {
      prior: priorPatient.gender,
      new: newPatient.gender,
      isDifferent: true,
      changeType: genderChangeType,
    };
  }

  // Compare birthDate
  const birthDateChangeType = getChangeType(
    priorPatient.birthDate,
    newPatient.birthDate,
  );
  if (birthDateChangeType) {
    diffMap.birthDate = {
      prior: priorPatient.birthDate,
      new: newPatient.birthDate,
      isDifferent: true,
      changeType: birthDateChangeType,
    };
  }

  // Compare addresses (filter out additionalDataFromRecords)
  const priorAddresses = filterAdditionalDataFromRecords(priorPatient.address);
  const newAddresses = filterAdditionalDataFromRecords(newPatient.address);
  const addressChangeType = getChangeType(priorAddresses, newAddresses);
  if (addressChangeType) {
    diffMap.address = {
      prior: priorAddresses,
      new: newAddresses,
      isDifferent: true,
      changeType: addressChangeType,
    };
  }

  // Compare telecoms (filter out additionalDataFromRecords)
  const priorTelecoms = filterAdditionalDataFromRecords(priorPatient.telecom);
  const newTelecoms = filterAdditionalDataFromRecords(newPatient.telecom);
  const telecomChangeType = getChangeType(priorTelecoms, newTelecoms);
  if (telecomChangeType) {
    diffMap.telecom = {
      prior: priorTelecoms,
      new: newTelecoms,
      isDifferent: true,
      changeType: telecomChangeType,
    };
  }

  // Compare identifiers
  const identifierChangeType = getChangeType(
    priorPatient.identifier,
    newPatient.identifier,
  );
  if (identifierChangeType) {
    diffMap.identifier = {
      prior: priorPatient.identifier,
      new: newPatient.identifier,
      isDifferent: true,
      changeType: identifierChangeType,
    };
  }

  // Compare deceased
  const deceasedChangeType = getChangeType(
    {
      deceasedBoolean: priorPatient.deceasedBoolean,
      deceasedDateTime: priorPatient.deceasedDateTime,
    },
    {
      deceasedBoolean: newPatient.deceasedBoolean,
      deceasedDateTime: newPatient.deceasedDateTime,
    },
  );
  if (deceasedChangeType) {
    diffMap.deceased = {
      prior: {
        deceasedBoolean: priorPatient.deceasedBoolean,
        deceasedDateTime: priorPatient.deceasedDateTime,
      },
      new: {
        deceasedBoolean: newPatient.deceasedBoolean,
        deceasedDateTime: newPatient.deceasedDateTime,
      },
      isDifferent: true,
      changeType: deceasedChangeType,
    };
  }

  // Compare multipleBirth
  const multipleBirthChangeType = getChangeType(
    {
      multipleBirthBoolean: priorPatient.multipleBirthBoolean,
      multipleBirthInteger: priorPatient.multipleBirthInteger,
    },
    {
      multipleBirthBoolean: newPatient.multipleBirthBoolean,
      multipleBirthInteger: newPatient.multipleBirthInteger,
    },
  );
  if (multipleBirthChangeType) {
    diffMap.multipleBirth = {
      prior: {
        multipleBirthBoolean: priorPatient.multipleBirthBoolean,
        multipleBirthInteger: priorPatient.multipleBirthInteger,
      },
      new: {
        multipleBirthBoolean: newPatient.multipleBirthBoolean,
        multipleBirthInteger: newPatient.multipleBirthInteger,
      },
      isDifferent: true,
      changeType: multipleBirthChangeType,
    };
  }

  // Compare maritalStatus
  const maritalStatusChangeType = getChangeType(
    priorPatient.maritalStatus,
    newPatient.maritalStatus,
  );
  if (maritalStatusChangeType) {
    diffMap.maritalStatus = {
      prior: priorPatient.maritalStatus,
      new: newPatient.maritalStatus,
      isDifferent: true,
      changeType: maritalStatusChangeType,
    };
  }

  // Extensions (race, ethnicity, etc.) are excluded from comparison

  return diffMap;
};

/**
 * Check if a specific field has differences
 * @param {Object} diffMap - The diff map from comparePatients
 * @param {string} field - The field name to check
 * @returns {Object|null} Diff info object or null
 */
export const getDiffInfo = (diffMap, field) => {
  return diffMap && diffMap[field] && diffMap[field].isDifferent
    ? diffMap[field]
    : null;
};

/**
 * Check if a specific field has differences (backward compatible)
 * @param {Object} diffMap - The diff map from comparePatients
 * @param {string} field - The field name to check
 * @returns {boolean} True if the field has differences
 */
export const hasDiff = (diffMap, field) => {
  return diffMap && diffMap[field] && diffMap[field].isDifferent;
};

/**
 * Get CSS class name for highlighting differences
 * @param {string} changeType - Type of change: 'added', 'removed', 'changed'
 * @param {boolean} isPrior - Whether this is the prior value
 * @returns {string} CSS class name
 */
export const getDiffClassName = (changeType, isPrior = false) => {
  if (!changeType) return "";

  if (isPrior) {
    return "preview-diff-prior";
  }

  switch (changeType) {
    case "added":
      return "preview-diff-added";
    case "removed":
      return "preview-diff-removed";
    case "changed":
      return "preview-diff-highlight";
    default:
      return "preview-diff-highlight";
  }
};
